import { useMediaUpload } from "@shared/hooks/useMediaUpload";
import { MediaFile, MediaType } from "@shared/types/media.types";
import theme from "@shared/utils/themeColors";
import { useState } from "react";
import { useDropzone } from "react-dropzone";

interface MediaUploaderProps {
  onUploadComplete: (files: MediaFile[]) => void;
  onError: (error: string) => void;
  maxFiles?: number;
  acceptType?: "image" | "all" | "document"; // "image" = only images, "all" = images and videos (default), "document" = pdf files
}

export function MediaUploader({
  onUploadComplete,
  onError,
  maxFiles = 4,
  acceptType = "all",
}: MediaUploaderProps) {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [error, setError] = useState<string | undefined>();
  const { uploadMedia } = useMediaUpload();

  const onDrop = async (acceptedFiles: File[]) => {
    // If only one file allowed and user selects more than one, show error and do not proceed
    if (maxFiles === 1 && acceptedFiles.length === 0) {
      const errorMsg = "You can only select one file.";
      setError(errorMsg);
      onError(errorMsg);
      return;
    }
    const filesToProcess = acceptedFiles.slice(0, maxFiles - files.length);

    for (const file of filesToProcess) {
      // Determine file type based on MIME type
      let fileType = MediaType.VIDEO;
      if (file.type.startsWith("image/")) {
        fileType = MediaType.IMAGE;
      } else if (file.type === "application/pdf") {
        fileType = MediaType.DOCUMENT;
      }

      const newFile: MediaFile = {
        file,
        preview: URL.createObjectURL(file),
        type: fileType,
        progress: 0,
      };

      setFiles((prevFiles) => [...prevFiles, newFile]);

      try {
        const uploadedUrl = await uploadMedia(newFile);

        const updatedFile = { ...newFile, uploadedUrl, progress: 100 };

        setFiles((prevFiles) =>
          prevFiles.map((f) => (f === newFile ? updatedFile : f))
        );
        onUploadComplete([updatedFile]);
      } catch (error) {
        console.error("Error uploading file:", error);
        setError(error instanceof Error ? error.message : "Upload failed");

        setFiles((prevFiles) =>
          prevFiles.map((f) =>
            f === newFile ? { ...f, error: "Upload failed" } : f
          )
        );

        onError(error instanceof Error ? error.message : "Upload failed 2");
      }
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept:
      acceptType === "image"
        ? { "image/*": [".jpg", ".jpeg", ".png", ".gif"] }
        : acceptType === "document"
        ? { "application/pdf": [".pdf"] }
        : {
            "image/*": [".jpg", ".jpeg", ".png", ".gif"],
            "video/*": [".mp4", ".webm"],
            "application/pdf": [".pdf"],
          },
    maxFiles,
  });

  const removeFile = (fileName: string) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.file.name !== fileName));
  };

  const retryUpload = async (fileData: MediaFile) => {
    try {
      const url = await uploadMedia(fileData);
      setFiles((currentFiles) =>
        currentFiles.map((f) =>
          f.file.name === fileData.file.name
            ? { ...f, progress: 100, error: undefined, uploadedUrl: url }
            : f
        )
      );
      onUploadComplete([fileData]);
      removeFile(fileData.file.name);
    } catch (error) {
      setFiles((currentFiles) =>
        currentFiles.map((f) =>
          f.file.name === fileData.file.name
            ? {
                ...f,
                error: error instanceof Error ? error.message : "Upload failed",
              }
            : f
        )
      );
      onError(error instanceof Error ? error.message : "Upload failed");
    }
  };

  return (
    <div>
      {/* Error message for maxFiles = 1 */}
      {error && (
        <div className={`${theme.text.error} text-center mb-2`}>{error}</div>
      )}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed ${theme.border.secondary} rounded-lg p-6 text-center cursor-pointer hover:border-violet-500 dark:hover:border-violet-400 transition-colors mt-4`}
      >
        <input {...getInputProps()} />
        <p className="text-gray-600 dark:text-gray-300">
          Drag and drop media files here, or click to select
        </p>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4">
        {files.map((file, index) => (
          <div key={index} className="relative">
            {file.type === MediaType.IMAGE ? (
              <img
                src={file.preview}
                alt="Preview"
                className="w-full h-32 object-cover rounded-lg"
              />
            ) : file.type === MediaType.VIDEO ? (
              <video
                src={file.preview}
                className="w-full h-32 object-cover rounded-lg"
              />
            ) : (
              <div className="w-full h-32 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg">
                <div className="flex flex-col items-center">
                  <svg
                    className="h-10 w-10 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="mt-1 text-xs text-gray-500 dark:text-gray-300">PDF</span>
                </div>
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1">
              {file.file.name}
            </div>

            {/* Progress or Error UI */}
            {!file.error && file.progress !== 100 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                <div className="h-1 w-3/4 bg-gray-200 rounded">
                  <div
                    className="h-full bg-primary-500 rounded"
                    style={{
                      width: `${file.progress}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Error State */}
            {file.error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50">
                <p className={`${theme.text.error} text-sm mb-2`}>
                  Upload failed
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => retryUpload(file)}
                    className={`${theme.bg.brand} text-white px-2 py-1 rounded text-xs hover:bg-violet-600`}
                  >
                    Retry
                  </button>
                  <button
                    onClick={() => removeFile(file.file.name)}
                    className={`${theme.bg.error} text-white px-2 py-1 rounded text-xs hover:bg-red-600`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            {/* Remove Button */}
            {!file.error && (
              <button
                onClick={() => removeFile(file.file.name)}
                className={`absolute -top-2 -right-2 ${theme.bg.error} text-white rounded-full p-1 hover:bg-red-600`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
